import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  PartnerDealInvestigationProgress,
  PartnerDealInvestigationResponse,
} from '@interfaces/partners/responses/partner-deal-briefing.response';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { PartnersHttpService } from '@services/http/partners/partners-http.service';
import { finalize, first } from 'rxjs';
import { describeBriefingError } from './briefing-errors';
import { mergeInvestigationProgress } from './briefing-presenter';

/**
 * Investigação da empresa do deal na aba Briefing (card 3911): a mais recente do CNPJ, o progresso ao vivo, o Refazer
 * e o Completar.
 *
 * - Só busca com `PARTNERDEALBRIEFINGINVESTIGATION_VIEW` (`start(dealId, canView)`): sem a permissão não sai GET
 *   nenhum, nem na abertura nem nos eventos de tempo real, e a caixa fica vazia (a aba a esconde).
 * - Sem a rota (backend antigo) ou com erro, fica sem investigação: ela é complemento, o briefing segue.
 * - Resposta de um deal que não é mais o aberto é descartada.
 *
 * Provido pela aba (`providers`), então o ciclo de vida acompanha a tela.
 */
@Injectable()
export class BriefingInvestigationStore {
  private readonly partnersHttp = inject(PartnersHttpService);
  private readonly toast = inject(ToastHelperService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly investigationState = signal<PartnerDealInvestigationResponse | null>(null);
  private readonly progressState = signal<PartnerDealInvestigationProgress | null>(null);
  private readonly rerunningState = signal(false);
  private readonly completingState = signal<string | null>(null);

  /** A investigação mais recente do CNPJ do deal; nula quando nunca houve (ou sem `VIEW`). */
  readonly investigation = this.investigationState.asReadonly();
  /** Progresso ao vivo da investigação que roda (tempo real). */
  readonly progress = this.progressState.asReadonly();
  readonly rerunning = this.rerunningState.asReadonly();
  /** Versão com o Completar investigação em andamento. */
  readonly completingVersionId = this.completingState.asReadonly();

  private dealId: string | null = null;
  private canView = false;

  /**
   * Deal aberto e se a pessoa pode ver a investigação. Troca de deal ou de permissão limpa o estado; com `VIEW`,
   * busca. A permissão começa em `false` e chega depois: o segundo `start` do mesmo deal é que busca.
   */
  start(dealId: string, canView: boolean): void {
    if (dealId === this.dealId && canView === this.canView) return;
    this.dealId = dealId;
    this.canView = canView;
    this.investigationState.set(null);
    this.progressState.set(null);
    this.refresh();
  }

  /** Relê a investigação (tempo real, depois de uma ação). Sem `VIEW`, não faz nada. */
  refresh(): void {
    const dealId = this.dealId;
    if (!dealId || !this.canView) return;
    this.partnersHttp
      .getDealInvestigation(dealId)
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: investigation => {
          if (this.dealId !== dealId || !this.canView) return;
          this.investigationState.set(investigation ?? null);
          // Rodando: o GET traz o progresso completo (etapas, atividade, subseções; card 3918) e se junta ao que a
          // caixa já mostra, sem voltar contador nem trocar por um progresso mais antigo.
          if (investigation?.status !== 'running') this.progressState.set(null);
          else if (investigation.progress) {
            this.progressState.set(
              mergeInvestigationProgress(this.progressState(), {
                ...investigation.progress,
                investigationId:
                  investigation.progress.investigationId ?? investigation.investigationId,
              })
            );
          }
        },
        error: () => undefined,
      });
  }

  /** Progresso ao vivo (evento de tempo real): fase e números na caixa; a primeira notícia relê o estado. */
  applyProgress(progress: PartnerDealInvestigationProgress | null): void {
    if (!this.canView) return;
    const current = this.investigationState();
    if (progress?.investigationId && current?.investigationId !== progress.investigationId) {
      this.refresh();
    } else if (current?.status === 'queued') {
      this.refresh();
    }
    // O evento é enxuto (fase e números): junta ao progresso do GET para não apagar atividade e subseções.
    this.progressState.set(mergeInvestigationProgress(this.progressState(), progress));
  }

  /** "Refazer investigação": pesquisa de novo com as subseções pedidas (as ligadas na configuração se nenhuma). */
  rerun(subsections: string[]): void {
    const dealId = this.dealId;
    if (!dealId || this.rerunningState()) return;
    this.rerunningState.set(true);
    this.partnersHttp
      .rerunDealInvestigation(dealId, subsections)
      .pipe(
        first(),
        finalize(() => this.rerunningState.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: result => {
          if (result && !result.isValid) {
            this.toast.showToastError(
              'Não foi possível refazer a investigação',
              result.results?.[0]?.message ?? result.message ?? 'Tente de novo em instantes.'
            );
            return;
          }
          if (result?.data) this.investigationState.set(result.data);
          this.progressState.set(null);
          this.toast.showToastSuccess(
            'Investigação pedida',
            'A pesquisa leva alguns minutos. O andamento aparece aqui sozinho.'
          );
        },
        error: err =>
          this.toast.showToastError(
            'Não foi possível refazer a investigação',
            describeBriefingError(err, 'Tente de novo em instantes.')
          ),
      });
  }

  /**
   * "Completar investigação": versão nova (`trigger = Investigacao`) que reescreve só Red flags e Recomendação com os
   * riscos da investigação que terminou depois do texto (decisão do backend, seção 2 do relatório da Q16).
   * `onRequested` roda quando o backend aceita (a aba relê as versões).
   */
  complete(versionId: string, onRequested: () => void): void {
    const dealId = this.dealId;
    if (!dealId || this.completingState()) return;
    this.completingState.set(versionId);
    this.partnersHttp
      .completeDealBriefingInvestigation(dealId, versionId)
      .pipe(
        first(),
        finalize(() => this.completingState.set(null)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.toast.showToastSuccess(
            'Completando a investigação',
            'Uma versão nova com os riscos da investigação em Red flags e Recomendação aparece aqui quando ficar pronta.'
          );
          onRequested();
        },
        error: err =>
          this.toast.showToastError(
            'Não foi possível completar a investigação',
            describeBriefingError(err, 'Tente de novo em instantes.')
          ),
      });
  }
}
